/*
  RevoRope — sección "Comunidad · eventos". Dos comportamientos, dos IIFE
  independientes en el mismo archivo porque comparten sección y nada más:

    A) LA GALERÍA de cada evento (hasta 5 fotos/vídeos con flechas manuales).
    B) EL "VER TODOS" de la rejilla de eventos anteriores.

  (Mismo criterio que assets/rr-nav.js, que ya lleva dos IIFE sin relación:
  mega-menú de escritorio y overlay móvil. No mezclar estados entre ellas.)

  --------------------------------------------------------------------------
  A) GALERÍA — por qué NO reutiliza rr-cinta.js ni rr-carousel.js

  Aquí no hay pista con scroll ni arco 3D: las piezas están APILADAS en el mismo
  hueco y sólo cambia cuál se ve. Es lo que pidió Oscar de forma explícita — "sin
  autoplay, sin scroll automático, sin inercia" — y de paso quita de raíz el
  riesgo de desbordamiento horizontal, porque nada se desplaza.

  Reparto de responsabilidades, igual que en las otras dos piezas: el CSS pinta
  el fundido y los estados; este controlador sólo decide CUÁL está activa,
  actualiza flechas/contador/región viva y decide QUÉ VÍDEO SE REPRODUCE.

  Lo del vídeo no es un adorno. Todos salen de Liquid con `preload="none"` y sin
  autoplay, así que no se descarga nada hasta que alguien lo pide. Un vídeo sólo
  arranca si se cumplen LAS DOS cosas: es la pieza activa Y la tarjeta está cerca
  del viewport. Con hasta 5 piezas por evento y varios eventos en la página, sin
  esta doble condición la página se traería decenas de megas de vídeo de golpe
  (es el mismo problema que ya obligó a escribir rr-lazy-video.js, cuando un
  único vídeo era el 46% del peso de la Home).

  Sin JS la tarjeta sigue enseñando su primera pieza (Liquid ya la marca
  `.is-activa`): se pierde la navegación, no el contenido.
*/
(function () {
  'use strict';

  function initGaleria(raiz) {
    var items = Array.prototype.slice.call(raiz.querySelectorAll('[data-rr-evgal-item]'));
    if (items.length < 2) return;

    var prev = raiz.querySelector('[data-rr-evgal-prev]');
    var next = raiz.querySelector('[data-rr-evgal-next]');
    var elIndice = raiz.querySelector('[data-rr-evgal-indice]');
    var elEstado = raiz.querySelector('[data-rr-evgal-estado]');
    var activo = 0;
    var cerca = false; // ¿la tarjeta está (casi) en pantalla?

    function media(item, enciende) {
      var v = item.querySelector('video');
      if (!v) return;
      if (enciende) {
        if (!v.dataset.rrEvgalLista) {
          v.dataset.rrEvgalLista = '1';
          v.preload = 'auto';
        }
        var p = v.play();
        if (p && p.catch) {
          p.catch(function () {
            /* Autoplay bloqueado por el navegador: se queda la portada. No hay
               nada que resolver desde aquí. */
          });
        }
      } else if (!v.paused) {
        v.pause();
      }
    }

    function pinta(anuncia) {
      for (var k = 0; k < items.length; k++) {
        var es = k === activo;
        items[k].classList.toggle('is-activa', es);
        // Fuera de la vista también fuera del árbol de accesibilidad: si no, un
        // lector de pantalla leería las cinco fotos como si estuvieran todas ahí.
        if (es) {
          items[k].removeAttribute('aria-hidden');
        } else {
          items[k].setAttribute('aria-hidden', 'true');
        }
        media(items[k], es && cerca);
      }
      if (elIndice) elIndice.textContent = String(activo + 1);

      /* Al llegar a un extremo se deshabilita la flecha de ese lado, y si el foco
         estaba justo en ella el navegador lo suelta al <body>: quien navegaba con
         teclado se queda sin galería a mitad de recorrido (bug real cazado en la
         verificación en vivo, no hipotético).

         El orden importa y es contraintuitivo: hay que aplicar PRIMERO los dos
         estados `disabled` y mover el foco DESPUÉS. Al revés no funciona, porque
         la flecha de destino todavía arrastra el `disabled` del paso anterior y
         un botón deshabilitado no acepta el foco. Con dos piezas o más nunca
         pueden estar las dos apagadas a la vez, así que siempre queda una viva. */
      var teniaFoco = document.activeElement;
      if (prev) prev.disabled = activo <= 0;
      if (next) next.disabled = activo >= items.length - 1;
      if (prev && next) {
        if (teniaFoco === prev && prev.disabled) next.focus();
        else if (teniaFoco === next && next.disabled) prev.focus();
      }
      if (anuncia && elEstado) {
        elEstado.textContent = 'Pieza ' + (activo + 1) + ' de ' + items.length;
      }
    }

    function vaA(i, anuncia) {
      var j = Math.max(0, Math.min(items.length - 1, i));
      if (j === activo) return;
      activo = j;
      pinta(anuncia);
    }

    if (prev) {
      prev.addEventListener('click', function () {
        vaA(activo - 1, true);
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        vaA(activo + 1, true);
      });
    }

    /* Teclado: con el foco dentro de la galería (o sea, sobre una de las dos
       flechas), las flechas del teclado también navegan. Es lo que espera quien
       llega aquí tabulando, y evita tener que ir y volver entre los dos botones
       para recorrer cinco piezas. */
    raiz.addEventListener('keydown', function (e) {
      var k = e.key;
      if (k === 'ArrowRight') {
        e.preventDefault();
        vaA(activo + 1, true);
      } else if (k === 'ArrowLeft') {
        e.preventDefault();
        vaA(activo - 1, true);
      } else if (k === 'Home') {
        e.preventDefault();
        vaA(0, true);
      } else if (k === 'End') {
        e.preventDefault();
        vaA(items.length - 1, true);
      }
    });

    /* La segunda condición del vídeo: cerca del viewport. Se mantiene observando
       (no se desconecta) para poder PAUSAR cuando la tarjeta se va, que es la
       mitad del ahorro. Una tarjeta todavía plegada por "ver todos" está en
       display:none, así que sencillamente no interseca hasta que se despliega. */
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entradas) {
        for (var i = 0; i < entradas.length; i++) {
          var v = entradas[i].isIntersecting;
          if (v === cerca) continue;
          cerca = v;
          pinta(false);
        }
      }, { root: null, rootMargin: '300px 0px', threshold: 0 });
      io.observe(raiz);
    } else {
      cerca = true;
    }

    pinta(false);
  }

  function arranca() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-rr-evgal]'), initGaleria);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', arranca);
  } else {
    arranca();
  }
})();

/*
  --------------------------------------------------------------------------
  B) "VER TODOS" — la rejilla de eventos anteriores enseña las primeras N y
  despliega el resto en la misma página (nada de paginación ni página aparte).

  El corte lo hace Liquid con `.rr-evento--extra`; aquí sólo se quita la clase.
  Es de una sola dirección a propósito: "Ver todos" es una promesa, y volver a
  plegar la lista debajo del dedo del usuario no aporta nada.

  El botón desaparece al usarse, así que hay que ocuparse del foco: si no, el
  foco se cae al <body> y quien navega con teclado o lector de pantalla pierde
  el sitio. Se lleva al primer evento recién revelado, que además es la
  confirmación de que ha pasado algo.
*/
(function () {
  'use strict';

  function init(boton) {
    var grupo = boton.closest('.rr-eventos__group');
    if (!grupo) return;

    boton.addEventListener('click', function () {
      var extras = Array.prototype.slice.call(grupo.querySelectorAll('.rr-evento--extra'));
      if (!extras.length) return;

      extras.forEach(function (li) {
        li.classList.remove('rr-evento--extra');
      });

      boton.setAttribute('aria-expanded', 'true');
      var caja = boton.parentNode;
      if (caja && caja.classList.contains('rr-eventos__mas-wrap')) {
        caja.hidden = true;
      } else {
        boton.hidden = true;
      }

      var destino = extras[0].querySelector('.rr-evento__nombre');
      if (destino && destino.focus) destino.focus();
    });
  }

  function arranca() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-rr-eventos-mas]'), init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', arranca);
  } else {
    arranca();
  }
})();
